import { useRef, useState } from "react";
import { results as defaultResults, resultsUrl } from "@/lib/site-data";
import { useHairLossSwap } from "@/lib/hair-loss-copy";

type ResultItem = {
  grafts: string;
  name: string;
  role: string;
  before: string;
  after: string;
  href?: string;
  fit?: "cover" | "contain";
};

export function Results({
  hideEyebrowMobile = false,
  items,
  initialCount,
  showSeeMore = false,
  batchSize,
  batchSizes,
  seeMoreLabel,
  eyebrow,
  heading,
  subheading,
  description,
  headlineAfterButton,
  columns = 4,
  resultsHref,
  resultsButtonLabel,
  showDisclaimer = false,
  nameTag = "div",
}: {
  hideEyebrowMobile?: boolean;
  items?: ResultItem[];
  initialCount?: number;
  showSeeMore?: boolean;
  batchSize?: number;
  batchSizes?: number[];
  seeMoreLabel?: string;
  eyebrow?: React.ReactNode;
  heading?: React.ReactNode;
  subheading?: React.ReactNode;
  description?: React.ReactNode | null;
  headlineAfterButton?: React.ReactNode;
  columns?: 3 | 4;
  resultsHref?: string;
  resultsButtonLabel?: string;
  showDisclaimer?: boolean;
  /** Tag of each client name. WordPress used h2. */
  nameTag?: "div" | "h2";
} = {}) {
  const NameTag = nameTag;
  const swap = useHairLossSwap();
  const all: ResultItem[] = items ?? defaultResults;
  const useBatches = Array.isArray(batchSizes) && batchSizes.length > 0;
  const initial = useBatches ? batchSizes![0] : (initialCount ?? all.length);
  const step = batchSize ?? initial;
  const [batchIndex, setBatchIndex] = useState(useBatches ? 1 : 0);
  const [count, setCount] = useState(initial);
  const visibleCount = Math.min(count, all.length);
  const visible = all.slice(0, visibleCount);
  const hasMore = visibleCount < all.length;
  const gridRef = useRef<HTMLDivElement | null>(null);
  const handleSeeMore = () => {
    const firstNewIndex = visibleCount;
    if (useBatches) {
      const next = batchSizes![batchIndex] ?? step;
      setCount((c) => c + next);
      setBatchIndex((i) => i + 1);
    } else {
      setCount((c) => c + step);
    }
    requestAnimationFrame(() => {
      const grid = gridRef.current;
      if (!grid) return;
      const tile = grid.children[firstNewIndex] as HTMLElement | undefined;
      if (tile) {
        const top = tile.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: "smooth" });
      }
    });
  };

  const ResultGrid = ({ results }: { results: ResultItem[] }) => (
    <div ref={gridRef} className={`grid gap-[15px] sm:grid-cols-2 ${columns === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"}`}>
      {results.map((r) => {
        const Wrapper: React.ElementType = r.href ? "a" : "div";
        return (
          <Wrapper
            key={r.name}
            data-result-card
            data-result-name={r.name}
            {...(r.href ? { href: r.href, target: "_blank", rel: "noopener" } : {})}
            className="group flex flex-col overflow-hidden bg-background shadow-sm transition hover:shadow-lg"
          >
            <div data-result-frame className="grid h-[250px] w-full grid-cols-2 overflow-hidden bg-white">
              <img
                src={r.before}
                alt={`${r.name} before hair transplant`}
                loading="lazy"
                width={500}
                height={500}
                className="block h-full w-full object-cover object-center"
              />
              <img
                src={r.after}
                alt={`${r.name} after hair transplant`}
                loading="lazy"
                width={500}
                height={500}
                className="block h-full w-full object-cover object-center"
              />
            </div>
            <div className="min-h-[139px] bg-foreground/70 px-[15px] py-[6px] text-background">
              <div className="text-[15px] font-bold leading-6 text-background">
                {r.grafts}
              </div>
              <NameTag className={`text-[30px] font-normal leading-[40px] ${r.href ? "underline underline-offset-4" : ""}`}>
                {r.name}
              </NameTag>
              <div className="mt-[1px] text-base italic leading-5 text-background">{r.role}</div>
              <div className="text-base italic leading-5 text-background">RESTORE® Client</div>
            </div>
          </Wrapper>
        );
      })}
    </div>
  );

  return (
    <section className="bg-background py-10 md:py-14">
      <div className={`mx-auto ${columns === 3 ? "max-w-[1280px] px-0" : "max-w-7xl px-4 lg:px-8"}`}>
        <p className={`text-center text-sm font-semibold tracking-wide text-foreground uppercase ${hideEyebrowMobile ? "hidden md:block" : ""}`}>
          {eyebrow ?? swap("Before & After Pictures of Hair Transplants")}
        </p>
        <h2 className="mx-auto mt-3 max-w-5xl text-center text-3xl font-extrabold leading-none text-foreground md:text-5xl">
          {heading ?? <>Real Clients, <span className="text-foreground">Real Results</span></>}
        </h2>
        {subheading ? (
          <h3 className="mx-auto mt-0 max-w-5xl text-center text-2xl font-extrabold leading-none text-foreground md:text-4xl">
            {subheading}
          </h3>
        ) : null}
        {description === null ? null : (
          <>
            <p className="mx-auto mt-6 max-w-4xl text-center text-base leading-relaxed text-muted-foreground">
              {description ?? (
                <>
                  {swap("Lasting results are the most significant indicator of a successful RESTORE® No-Shave FUE™ Hair Transplant procedure. From celebrities and athletes to businessmen and every man, RESTORE's hair transplant before and after results are a testament to our expertise with FUE procedures that do not require shaving your head.")}
                </>
              )}
            </p>
            <p className="mx-auto mt-3 max-w-4xl text-center text-sm italic text-muted-foreground">
              100% authentic RESTORE clients. No photoshopping. No trick photography. No Stock photos.
            </p>
          </>
        )}
        <div className="mt-12">
          <ResultGrid results={visible} />
        </div>
        {showDisclaimer ? (
          <p className="mx-auto mt-8 max-w-5xl text-center text-xs italic leading-relaxed text-muted-foreground">
            Not all patients will experience the outcomes shown in the photographs and described in
            the testimonial statements on this website, and not all persons will be eligible for the
            procedure. Results will depend on a number of factors, including your personal medical
            history. Please ask a physician about your individual case. Testimonials were compensated
            for their endorsements.
          </p>
        ) : null}
        <div className="mt-8 text-center">
          {showSeeMore ? (
            hasMore ? (
              <button
                type="button"
                onClick={handleSeeMore}
                className="inline-block bg-primary px-10 py-4 text-sm font-bold tracking-[0.2em] text-primary-foreground transition hover:bg-primary/90"
              >
                {seeMoreLabel ?? swap("See more hair restoration results")}
              </button>
            ) : null
          ) : hasMore || resultsHref ? (
            <a
              href={resultsHref ?? resultsUrl}
              className="inline-block bg-primary px-8 py-4 text-sm font-bold tracking-wider text-primary-foreground transition hover:bg-primary/90"
            >
              {resultsButtonLabel ?? "See All Before & Afters"}
            </a>
          ) : null}
        </div>
        {headlineAfterButton && showSeeMore && hasMore ? (
          <div className="mx-auto mt-10 max-w-5xl text-center">
            {headlineAfterButton}
          </div>
        ) : null}
        {showSeeMore ? (
          <p className="mx-auto mt-10 max-w-5xl text-center text-xs italic leading-relaxed text-muted-foreground">
            Not all patients will experience the outcomes shown in the photographs and described in
            the testimonial statements on this website, and not all persons will be eligible for the
            procedure. Results will depend on a number of factors, including your personal medical
            history. Please ask a physician about your individual case. Testimonials were compensated
            for their endorsements.
          </p>
        ) : null}
      </div>
    </section>
  );
}
